import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { FaUser, FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt, FaLock, FaShieldAlt, FaCalendarAlt, FaCheckCircle, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { toast } from 'react-toastify';
import CompanyUsersCard from '../components/CompanyUsersCard';

// Composes the company address from its parts without repeating words
// (physical_address often already contains the city and postal code).
const clientAddress = (c) => {
  if (!c) return null;
  const parts = [c.physical_address, c.city, c.province, c.postal_code].filter(Boolean);
  const out = [];
  parts.forEach((p) => {
    const t = String(p).trim();
    if (t && !out.some((o) => o.toLowerCase().includes(t.toLowerCase()))) out.push(t);
  });
  return out.join(', ') || null;
};

function ClientProfile({ onLogout }) {
  const [user, setUser] = useState({});
  const [client, setClient] = useState(null);
  const [me, setMe] = useState({ full_name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMe, setSavingMe] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  // The company's own login (the primary contact) maintains the COMPANY record.
  // Any other person at the company only maintains their OWN name/email/cell.
  const isPrimary = !!(client?.user_id && client.user_id === currentUser.id);
  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`
  });

  useEffect(() => {
    setUser(currentUser);
    fetchProfile(currentUser);
    fetchMe(currentUser);
  }, []);

  // My own login record (name / email / cell) — GET /users/me
  const fetchMe = async (userData) => {
    try {
      const r = await fetch(`${API_URL}/users/me`, { headers: authHeaders() });
      const d = await r.json();
      const u = d.user || d || {};
      setMe({
        full_name: u.full_name || userData.full_name || '',
        email: u.email || userData.email || '',
        phone: u.phone || ''
      });
    } catch {
      setMe({ full_name: userData.full_name || '', email: userData.email || '', phone: '' });
    }
  };

  const handleMeChange = (e) => setMe((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const saveMe = async (e) => {
    e.preventDefault();
    setSavingMe(true);
    try {
      const r = await fetch(`${API_URL}/users/me`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ full_name: me.full_name, phone: me.phone })
      });
      const d = await r.json();
      if (d.success) toast.success('✅ Your details saved');
      else toast.error(d.error || 'Could not save your details');
    } catch {
      toast.error('Could not save your details');
    } finally {
      setSavingMe(false);
    }
  };

  const fetchProfile = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/clients?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.clients && data.clients.length > 0) {
        const clientData = data.clients[0];
        setClient(clientData);
        setFormData({
          company_name: clientData.company_name || '',
          contact_person: clientData.contact_person || '',
          email: clientData.contact_email || clientData.email || '',
          phone: clientData.contact_phone || clientData.phone || '',
          address: clientData.physical_address || '',
          city: clientData.city || '',
          province: clientData.province || '',
          postal_code: clientData.postal_code || '',
          registration_number: clientData.registration_number || '',
          vat_number: clientData.vat_number || ''
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/clients/${client.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.company_name,
          contact_person: formData.contact_person,
          contact_email: formData.email,
          contact_phone: formData.phone,
          physical_address: formData.address,
          city: formData.city,
          province: formData.province,
          postal_code: formData.postal_code,
          registration_number: formData.registration_number,
          vat_number: formData.vat_number
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Profile updated successfully!');
        setEditMode(false);
        fetchProfile(user);
      } else {
        toast.error(data.error || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ClientLayout activeTab="profile" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading profile...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout activeTab="profile" onLogout={onLogout} user={user}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>👤 My Profile</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>View and manage your company profile</p>
        </div>
        <button
          onClick={() => setShowPasswordModal(true)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#2563EB',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1D4ED8'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
        >
          <FaLock size={14} /> Change Password
        </button>
      </div>

      {/* ── MY OWN DETAILS: name / email / cell (the only things a person fills in) ── */}
      <form onSubmit={saveMe}>
        <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
          <h3 className="font-semibold text-navy mb-1">Your Details</h3>
          <p className="text-xs text-gray-500 mb-4">
            Your own login details. The company information below is shared with everyone at {client?.company_name || 'your company'}.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <div className="flex items-center gap-2">
                <FaUser className="text-gray-400" />
                <input
                  type="text"
                  name="full_name"
                  value={me.full_name}
                  onChange={handleMeChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email (your login)</label>
              <div className="flex items-center gap-2">
                <FaEnvelope className="text-gray-400" />
                <input
                  type="email"
                  value={me.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cell Number</label>
              <div className="flex items-center gap-2">
                <FaPhone className="text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={me.phone}
                  onChange={handleMeChange}
                  placeholder="e.g. +27 82 123 4567"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              disabled={savingMe}
              className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {savingMe ? <div className="animate-spin">⏳</div> : <FaSave size={14} />}
              {savingMe ? 'Saving...' : 'Save my details'}
            </button>
          </div>
        </div>
      </form>

      <form onSubmit={handleSave}>
        <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-navy">Company Information</h3>
            {isPrimary ? (
              <button
                type="button"
                onClick={() => setEditMode(!editMode)}
                className="flex items-center gap-2 px-3 py-1 border border-gray-200 rounded-lg text-sm hover:border-green hover:text-green transition-colors"
              >
                {editMode ? <FaTimes size={14} /> : <FaEdit size={14} />}
                {editMode ? 'Cancel' : 'Edit'}
              </button>
            ) : (
              <span className="text-xs text-gray-400">
                Managed by {client?.contact_person || 'your company contact'} or One Purpose — ask them to change it
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              {editMode ? (
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
              ) : (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FaBuilding className="text-gray-400" />
                  <span>{client?.company_name || 'N/A'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
              {editMode ? (
                <input
                  type="text"
                  name="registration_number"
                  value={formData.registration_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
              ) : (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FaBuilding className="text-gray-400" />
                  <span>{client?.registration_number || 'N/A'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
              {editMode ? (
                <input
                  type="text"
                  name="vat_number"
                  value={formData.vat_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
              ) : (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FaBuilding className="text-gray-400" />
                  <span>{client?.vat_number || 'N/A'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
              {editMode ? (
                <input
                  type="text"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
              ) : (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FaUser className="text-gray-400" />
                  <span>{client?.contact_person || 'N/A'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              {editMode ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
              ) : (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FaEnvelope className="text-gray-400" />
                  <span>{client?.email || client?.contact_email || 'N/A'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              {editMode ? (
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                />
              ) : (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FaPhone className="text-gray-400" />
                  <span>{client?.phone || client?.contact_phone || 'N/A'}</span>
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business address</label>
              {editMode ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="sm:col-span-2 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    rows="2"
                    placeholder="Street address"
                  />
                  <input
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      name="province"
                      value={formData.province}
                      onChange={handleInputChange}
                      placeholder="Province"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    />
                    <input
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      placeholder="Postal code"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                  <FaMapMarkerAlt className="text-gray-400 mt-1" />
                  <span>{clientAddress(client) || 'No address on file'}</span>
                </div>
              )}
            </div>
          </div>

          {editMode && (
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setEditMode(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors flex items-center gap-2"
              >
                {saving ? <div className="animate-spin">⏳</div> : <FaSave size={14} />}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </form>

      {/* ✅ PHASE 2 — the primary contact maintains their own company's logins */}
      {isPrimary && client && (
        <div className="mt-6">
          <CompanyUsersCard
            companyType="client"
            companyId={client.id}
            companyName={client.company_name}
            className="rounded-xl border p-6"
          />
        </div>
      )}

      <div className="mt-6 bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
          <FaShieldAlt className="text-green" /> Account Information
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Account Created</span>
            <span className="text-gray-700">{new Date(user?.created_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Last Login</span>
            <span className="text-gray-700">{user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-500">Status</span>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user?.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
              {user?.is_active ? '✅ Active' : '❌ Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userId={user.id}
      />
    </ClientLayout>
  );
}

export default ClientProfile;
